import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Checkbox, Group, Loader, ScrollArea } from '@mantine/core'
import { IconAlertTriangle, IconArrowRight } from '@tabler/icons-react'
import { cn } from '@/utils/cn'
import { useClient, type OrpcClient } from '@/hooks/useClient'
import { formatCalendarDay, formatWallClockTime } from './zocdocFormat'
import { HINT, ID_CHIP, PRIMARY_BTN, SECONDARY_BTN } from './tokens'

type OptionsResult = Awaited<ReturnType<OrpcClient['zocdocRescheduleOptions']>>
type RescheduleResult = Awaited<ReturnType<OrpcClient['zocdocRescheduleAppointment']>>
type OptionDay = OptionsResult['days'][number]
type OptionSlot = OptionDay['slots'][number]

/** Days of Zocdoc availability offered. One upstream request per day, so the window is bounded. */
const WINDOW_DAYS = 14

/** `YYYY-MM-DD`, `offset` days from today. Character surgery on a UTC date — no zone arithmetic. */
const dayOffset = (offset: number) => {
  const date = new Date()
  date.setDate(date.getDate() + offset)
  return date.toISOString().slice(0, 10)
}

/**
 * How a candidate slot reads, and whether it can be taken at all.
 *
 * `claimed` is the one that blocks: a slot that exists and is taken means an appointment already
 * occupies that time, and rescheduling onto it would double-book the practitioner. It is shown
 * rather than hidden — an operator looking for a time needs to know why a slot Zocdoc offers is
 * not available here.
 */
const SLOT_STATE: Record<string, { tone: string; note: string; blocked: boolean }> = {
  free: {
    tone: 'border-[#BFDBFE] bg-white hover:border-[#E5533D] hover:bg-[#FCF1EE]',
    note: 'slot free',
    blocked: false,
  },
  'no-slot': {
    tone: 'border-[#FDE68A] bg-white hover:border-[#E5533D] hover:bg-[#FCF1EE]',
    note: 'no slot — can be created',
    blocked: false,
  },
  claimed: {
    tone: 'border-[#FECACA] bg-[#FEF2F2] cursor-not-allowed opacity-70',
    note: 'conflicts with another booking',
    blocked: true,
  },
  'no-schedule': {
    tone: 'border-[#E7E9EE] bg-[#F8F9FB] cursor-not-allowed opacity-70',
    note: 'practitioner has no schedule',
    blocked: true,
  },
}

/** One row of the was/now comparison. */
function Compare({
  label,
  was,
  now,
}: {
  label: string
  was: React.ReactNode
  now: React.ReactNode
}) {
  return (
    <div className="grid grid-cols-[110px_1fr_18px_1fr] items-center gap-2 border-b border-[#ECEEF1] py-2 last:border-b-0">
      <span className="text-[12px] font-bold text-[#6B7280]">{label}</span>
      <span className="text-[13px] text-[#6B7280] line-through">{was}</span>
      <IconArrowRight size={14} className="text-[#9AA0A8]" />
      <span className="text-[13px] font-bold text-[#1F2733]">{now}</span>
    </div>
  )
}

/**
 * The reschedule flow: pick a slot Zocdoc offers, then confirm the move against what beHuman
 * currently holds.
 *
 * Its own component rather than a third branch inside the action sheet, which already carries two
 * steps and four actions. The two halves here — a slot picker and a comparison — have their own
 * state and their own loading, and inlining them would make the sheet the largest thing on the
 * page by some margin.
 *
 * **Every option is annotated server-side.** The conflict answer comes from the same
 * schedule-scoped `Slot` lookup the write performs, so a slot this screen offers is one the write
 * will accept — and a slot it blocks is one the write would refuse.
 */
export function RescheduleSteps({
  zocdocAppointmentId,
  patientName,
  onBack,
  onDone,
}: {
  zocdocAppointmentId: string
  patientName: string
  onBack: () => void
  /** A completed reschedule; the caller re-reads the list and reports the outcome. */
  onDone: (result: RescheduleResult) => void
}) {
  const client = useClient()
  const [options, setOptions] = useState<OptionsResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [failure, setFailure] = useState<string | null>(null)
  const [picked, setPicked] = useState<{ day: string; slot: OptionSlot } | null>(null)
  const [createSlot, setCreateSlot] = useState(false)
  const [sending, setSending] = useState(false)

  const fromDay = useMemo(() => dayOffset(0), [])
  const toDay = useMemo(() => dayOffset(WINDOW_DAYS - 1), [])

  // Loads on mount: the operator asked for a reschedule, so the slots are the answer to a question
  // already asked rather than something to click again for.
  const load = useCallback(async () => {
    setLoading(true)
    setFailure(null)
    try {
      setOptions(await client.zocdocRescheduleOptions({ zocdocAppointmentId, fromDay, toDay }))
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : 'Could not reach middleware')
    } finally {
      setLoading(false)
    }
  }, [client, zocdocAppointmentId, fromDay, toDay])

  useEffect(() => {
    void load()
  }, [load])

  // A new pick is a new decision: the checkbox belongs to the slot it was ticked for, and carrying
  // it across would publish availability at a time the operator never chose it for.
  const pick = (day: string, slot: OptionSlot) => {
    setPicked({ day, slot })
    setCreateSlot(false)
  }

  const confirm = async () => {
    if (!picked) return
    setSending(true)
    setFailure(null)
    try {
      onDone(
        await client.zocdocRescheduleAppointment({
          zocdocAppointmentId,
          day: picked.day,
          // The raw `HH:mm` the options read returned — never the formatted display string.
          time: picked.slot.time,
          ...(picked.slot.timeZone && { timeZone: picked.slot.timeZone }),
          ...(picked.slot.locationId && { locationId: picked.slot.locationId }),
          ...(createSlot && { createSlot: true }),
        }),
      )
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : 'Could not reach middleware')
    } finally {
      setSending(false)
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center gap-2 py-10">
        <Loader size="sm" color="#E5533D" />
        <p className={cn([HINT, 'm-0'])}>
          Reading {patientName}’s provider availability from Zocdoc — one request per day, so this
          takes a moment.
        </p>
      </div>
    )
  }

  if (failure) {
    return (
      <>
        <Alert color="red" radius="md" title="Could not load the options">
          <span className="text-[13px]">{failure}</span>
        </Alert>
        <Group justify="space-between" gap={8}>
          <Button {...SECONDARY_BTN} onClick={onBack}>
            Back
          </Button>
          <Button {...SECONDARY_BTN} onClick={() => void load()}>
            Try again
          </Button>
        </Group>
      </>
    )
  }

  // A refusal answers the whole step: there is no point offering slots for an appointment Zocdoc
  // will not move.
  if (options?.refusal) {
    return (
      <>
        <Alert color="yellow" radius="md" title="This booking cannot be rescheduled">
          <span className="text-[13px]">{options.refusal.detail}.</span>
        </Alert>
        <Group justify="flex-start">
          <Button {...SECONDARY_BTN} onClick={onBack}>
            Back
          </Button>
        </Group>
      </>
    )
  }

  const current = options?.current
  const days = options?.days ?? []
  const bookable = days.reduce(
    (total, day) =>
      total + day.slots.filter((slot) => !SLOT_STATE[slot.behuman.state]?.blocked).length,
    0,
  )

  // Step two: the comparison. Deliberately a separate screen rather than a confirm dialog over the
  // picker — this is the last point before an unretryable write, and it is worth reading.
  if (picked) {
    const state = SLOT_STATE[picked.slot.behuman.state]
    const needsSlot = picked.slot.behuman.state === 'no-slot'
    return (
      <>
        <div className="rounded-xl border border-[#E7E9EE] bg-white px-3 py-1">
          <Compare
            label="Date"
            was={current?.startAt ? formatCalendarDay(current.startAt.slice(0, 10)) : '—'}
            now={formatCalendarDay(picked.day)}
          />
          <Compare
            label="Time"
            was={current?.startAt ? formatWallClockTime(current.startAt.slice(11, 16)) : '—'}
            now={formatWallClockTime(picked.slot.time)}
          />
          <Compare
            label="Stored (UTC)"
            was={<span className={ID_CHIP}>{current?.startAt ?? '—'}</span>}
            now={<span className={ID_CHIP}>{picked.slot.utcInstant ?? '—'}</span>}
          />
          {picked.slot.locationName && (
            <Compare label="Location" was="—" now={picked.slot.locationName} />
          )}
        </div>

        <p className={cn([HINT, 'm-0'])}>
          {current?.practitionerName
            ? `Both sides move: the Zocdoc booking and ${current.practitionerName}’s beHuman visit.`
            : 'Both sides move: the Zocdoc booking and the bound beHuman visit.'}{' '}
          {state?.note === 'slot free' && 'A free slot already exists at the new time.'}
        </p>

        {needsSlot && (
          <Checkbox
            checked={createSlot}
            onChange={(event) => setCreateSlot(event.currentTarget.checked)}
            label="Publish a slot at the new time"
            // Named for what it actually does: this commits a practitioner to a time they never
            // offered, which is a different thing from filling one they did.
            description={`${current?.practitionerName ?? 'The practitioner'} has published no availability at this time. Ticking this creates a ${current?.durationMinutes ?? 30}-minute slot on their calendar so the visit can move into it.`}
            classNames={{ label: 'behuman-v2-mulish !text-[13px] !font-bold' }}
          />
        )}

        {needsSlot && !createSlot && (
          <Alert color="yellow" radius="md" icon={<IconAlertTriangle size={16} />}>
            <span className="text-[13px]">
              Without a slot at the new time the reschedule will be refused, and nothing is sent to
              Zocdoc.
            </span>
          </Alert>
        )}

        <p className={cn([HINT, 'm-0'])}>
          Zocdoc has no undo for a reschedule, so this is sent once. If Zocdoc accepts it and the
          beHuman move then fails, you are told exactly that rather than it being retried.
        </p>

        <Group justify="space-between" gap={8}>
          <Button {...SECONDARY_BTN} onClick={() => setPicked(null)} disabled={sending}>
            Pick another time
          </Button>
          <Button
            {...PRIMARY_BTN}
            loading={sending}
            disabled={needsSlot && !createSlot}
            onClick={() => void confirm()}
          >
            Reschedule
          </Button>
        </Group>
      </>
    )
  }

  // Step one: the picker.
  return (
    <>
      <p className={cn([HINT, 'm-0'])}>
        {bookable > 0
          ? `${bookable} time${bookable === 1 ? '' : 's'} Zocdoc offers over the next ${WINDOW_DAYS} days, checked against ${current?.practitionerName ?? 'the practitioner'}’s beHuman calendar.`
          : `Zocdoc offers nothing bookable for this provider over the next ${WINDOW_DAYS} days.`}
      </p>

      <ScrollArea.Autosize mah={340} type="auto">
        <div className="flex flex-col gap-3 pr-1">
          {days
            .filter((day) => day.slots.length > 0 || day.error)
            .map((day) => (
              <div key={day.date}>
                <p className="behuman-v2-mulish m-0 mb-1 text-[11px] font-bold tracking-[0.06em] text-[#6B7280] uppercase">
                  {formatCalendarDay(day.date)}
                </p>
                {day.error ? (
                  // Not folded into "no slots": a failed read and an empty day are different
                  // answers, and one of them is worth retrying.
                  <p className={cn([HINT, 'm-0'])}>Could not read this day — {day.error}</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {day.slots.map((slot) => {
                      const state = SLOT_STATE[slot.behuman.state]
                      const blocked = state?.blocked ?? true
                      return (
                        <button
                          key={`${day.date}-${slot.time}`}
                          type="button"
                          disabled={blocked}
                          title={slot.behuman.detail ?? state?.note}
                          onClick={() => pick(day.date, slot)}
                          className={cn([
                            'flex cursor-pointer flex-col items-start gap-0.5 rounded-lg border px-2.5 py-1.5 text-left transition-colors',
                            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E5533D]',
                            state?.tone ?? 'border-[#E7E9EE] bg-white',
                          ])}
                        >
                          <span className="behuman-v2-mulish text-[13px] font-bold text-[#1F2733]">
                            {formatWallClockTime(slot.time)}
                          </span>
                          <span className="text-[10.5px] text-[#6B7280]">{state?.note}</span>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            ))}
        </div>
      </ScrollArea.Autosize>

      <p className={cn([HINT, 'm-0'])}>
        A time marked{' '}
        <span className="font-bold text-[#DC2626]">conflicts with another booking</span> cannot be
        chosen — an appointment already holds it on the beHuman calendar, and moving onto it would
        double-book the practitioner.
      </p>

      <Group justify="flex-start" gap={8}>
        <Button {...SECONDARY_BTN} onClick={onBack}>
          Back
        </Button>
      </Group>
    </>
  )
}
